import { frontalQuad, horizontalQuad } from '../geometry';
import type { Camera } from '../geometry';
import type { SceneRegion } from './types';

/**
 * Staircase — a straight flight of seven risers between two walls, rising to a landing lit from the
 * far end. Metres; the camera stands 2.4 m before the first riser, at eye height 1.75 m.
 */
export const staircaseCamera: Camera = { eye: [0, 1.75, -2.4], focal: 880, center: [600, 215] };

export const STAIRS = {
  steps: 7,
  rise: 0.18,
  going: 0.34,
  /** Half width: the flight runs wall to wall. */
  half: 1.25,
  /** Depth of the back wall. */
  zBack: 5,
  ceiling: 3.5,
} as const;

const { steps, rise, going, half, zBack } = STAIRS;

/** Depth of the front of step `i` and height of its top. */
export const stepZ = (i: number): number => i * going;
export const stepY = (i: number): number => i * rise;

/** A texture offset that differs for every surface (golden-ratio steps), so no two show the same patch. */
const offsetFor = (index: number): readonly [number, number] => [
  ((index * 0.618) % 1) * 0.7,
  ((index * 0.382 + 0.3) % 1) * 0.7,
];

function buildRegions(): SceneRegion[] {
  const regions: SceneRegion[] = [
    {
      id: 'landing',
      quad: horizontalQuad(staircaseCamera, -half, half, stepZ(steps - 1), zBack, stepY(steps)),
      size: [2 * half, zBack - stepZ(steps - 1)],
      tile: 0.75,
      bleed: 1,
      offset: offsetFor(30),
    },
  ];
  // far to near: each step's tread, then its riser (the last tread is the start of the landing)
  for (let i = steps - 1; i >= 0; i -= 1) {
    if (i < steps - 1) {
      regions.push({
        id: `tread-${String(i)}`,
        quad: horizontalQuad(staircaseCamera, -half, half, stepZ(i), stepZ(i + 1), stepY(i + 1)),
        size: [2 * half, going],
        tile: 0.75,
        bleed: 1.1,
        offset: offsetFor(2 * i),
      });
    }
    regions.push({
      id: `riser-${String(i)}`,
      quad: frontalQuad(staircaseCamera, -half, half, stepY(i), stepY(i + 1), stepZ(i)),
      size: [2 * half, rise],
      tile: 0.75,
      bleed: 1.1,
      offset: offsetFor(2 * i + 1),
    });
  }
  return regions;
}

export const staircaseRegions: readonly SceneRegion[] = buildRegions();
